import { useState } from "react";
import { Container, Hero, Lightbox, Button } from "@/components";
import styles from "./Gallery.module.css";
import { galleryImages } from "@/data";
import { ZoomIn } from "lucide-react";
import clsx from "clsx";

const Gallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // Get unique categories from gallery images
  const categories = [
    "all",
    ...new Set(galleryImages.map((image) => image.category)),
  ];

  // Filter images based on selected category
  const filteredImages =
    selectedCategory === "all"
      ? galleryImages
      : galleryImages.filter((image) => image.category === selectedCategory);

  const handleImageClick = (imageIndex: number) => {
    // Find the original index in the full gallery array
    const clickedImage = filteredImages[imageIndex];
    const originalIndex = galleryImages.findIndex(
      (img) => img.id === clickedImage.id
    );
    setCurrentImageIndex(originalIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const handleLightboxNavigate = (direction: "next" | "prev") => {
    if (direction === "next" && currentImageIndex < galleryImages.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    } else if (direction === "prev" && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  // Category display names
  const getCategoryDisplayName = (category: string) => {
    const displayNames: { [key: string]: string } = {
      all: "All Projects",
      driveway: "Driveways",
      patio: "Pool Decks & Patios",
      sidewalk: "Sidewalks & Walkways",
      slabs: "Garage Slabs & Pads",
      custom: "Custom Work",
    };
    return displayNames[category] || category;
  };

  return (
    <>
      {/* Hero Section */}
      <Hero
        title="Our Project Gallery"
        subtitle="Take a look at some of our recent concrete projects."
        backgroundImage="./img/hero4.png"
      >
        <Button href="/contact" variant="primary" size="lg">
          Get a Quote
        </Button>
        <Button href="/services" variant="secondary" size="lg">
          Our Services
        </Button>
      </Hero>

      {/* Gallery Section */}
      <section className={styles.gallerySection}>
        <Container>
          <div className={styles.galleryContent}>
            <h2 className={styles.galleryTitle}>Recent Projects</h2>
            <p className={styles.galleryDescription}>
              Browse through our portfolio of completed concrete projects. From
              driveways and patios to sidewalks and custom work, we take pride
              in delivering quality results for our customers.
            </p>

            {/* Category Filter */}
            <div className={styles.categoryFilter}>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={clsx(
                    styles.categoryButton,
                    selectedCategory === category
                      ? styles.categoryButtonActive
                      : styles.categoryButtonInactive
                  )}
                >
                  {getCategoryDisplayName(category)}
                </button>
              ))}
            </div>

            {/* Gallery Grid */}
            <div className={styles.galleryGrid}>
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className={styles.galleryItem}
                  onClick={() => handleImageClick(index)}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className={styles.galleryImage}
                    loading="lazy"
                  />
                  <div className={styles.imageOverlay}>
                    <ZoomIn size={24} color="var(--color-text)" />
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {filteredImages.length === 0 && (
              <div className={styles.emptyState}>
                <h3 className={styles.emptyStateTitle}>No projects found</h3>
                <p className={styles.emptyStateText}>
                  Try selecting a different category or check back later for new
                  projects.
                </p>
              </div>
            )}
          </div>
        </Container>
      </section>

      {/* Lightbox */}
      <Lightbox
        images={galleryImages}
        isOpen={lightboxOpen}
        currentIndex={currentImageIndex}
        onClose={closeLightbox}
        onNavigate={handleLightboxNavigate}
      />
    </>
  );
};

export default Gallery;
